import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TwoFactorPanel from "@/components/TwoFactorPanel";

/**
 * Client-side mirror of backend strength rules. Backend re-validates anyway.
 */
function checkStrength(pw) {
  if (!pw || pw.length < 12) return "Use at least 12 characters.";
  if (!/[a-z]/.test(pw)) return "Add a lowercase letter.";
  if (!/[A-Z]/.test(pw)) return "Add an uppercase letter.";
  if (!/\d/.test(pw)) return "Add a number.";
  if (!/[^A-Za-z0-9]/.test(pw)) return "Add a symbol.";
  for (const w of ["password", "qwerty", "letmein", "iloveyou", "admin", "abbotasia", "changeme", "welcome", "trustno"]) {
    if (pw.toLowerCase().includes(w)) return "Avoid common words like 'password', 'admin', 'welcome'.";
  }
  return null;
}

export default function AccountSettings() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const localIssue =
    !current ? "Enter your current password." :
    !next ? null :
    next !== confirm ? "New password and confirmation do not match." :
    checkStrength(next);

  const submit = async (e) => {
    e.preventDefault();
    if (localIssue && localIssue !== "Enter your current password.") {
      toast.error(localIssue);
      return;
    }
    setBusy(true);
    try {
      await api.put("/admin/me/password", {
        current_password: current,
        new_password: next,
        confirm_password: confirm,
      });
      toast.success("Password changed. Please sign in again.");
      // Sessions are invalidated on the server. Clear our local user.
      setUser(null);
      setTimeout(() => navigate("/studio/login", { replace: true }), 600);
    } catch (err) {
      // Use server's safe message verbatim
      toast.error(err?.response?.data?.detail || "Could not change password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-account">
      <p className="eyebrow">Security</p>
      <h1 className="mt-3 font-serif text-4xl text-jade font-light">Account settings</h1>
      <p className="mt-3 text-ink/65 text-[14px] font-light max-w-xl">
        Signed in as <span className="text-ink/85">{user?.email}</span>. Change your password below. All other admin sessions will be signed out.
      </p>

      <form onSubmit={submit} className="mt-12 space-y-8 max-w-lg">
        <div>
          <label className="eyebrow text-ink/55">Current password</label>
          <input
            data-testid="account-current"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            autoComplete="current-password"
            className="input-line mt-3"
          />
        </div>
        <div>
          <label className="eyebrow text-ink/55">New password</label>
          <input
            data-testid="account-new"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            autoComplete="new-password"
            className="input-line mt-3"
          />
          <p className="mt-2 text-[12px] text-ink/50">
            At least 12 characters with uppercase, lowercase, number, and symbol. Avoid common words.
          </p>
        </div>
        <div>
          <label className="eyebrow text-ink/55">Confirm new password</label>
          <input
            data-testid="account-confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
            className="input-line mt-3"
          />
        </div>
        {next && localIssue && localIssue !== "Enter your current password." && (
          <p className="text-[13px] text-copper" data-testid="account-issue">{localIssue}</p>
        )}
        <div className="pt-2">
          <button
            data-testid="account-submit"
            type="submit"
            disabled={busy || !!localIssue}
            className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Changing…" : "Change password"}
          </button>
        </div>
      </form>

      <TwoFactorPanel />
    </div>
  );
}
